import type { TFunction } from "i18next";
import type { PartnerSettings } from "@/types/partner-settings";
import type {
  GuesteriaPartnerDestinationType,
  GuesteriaPartnerDetail,
  GuesteriaPartnerRow,
} from "./guesteriaPartnersModel";

/**
 * Task 0028, contract 2026-09-28: TENANT_ADMIN/HOTEL_ADMIN/FRONT_DESK never
 * see a provider name or logo — the API gives them `source:
 * "guesteria_partners"` instead of `provider`, and the UI shows one fixed
 * "Guesteria Partners" identity regardless. There is exactly one row here
 * (this hotel's own settings), so a fixed id needs no provider to derive
 * from either.
 */
const GUESTERIA_PARTNERS_ROW_ID = "guesteria-partners";

function destinationTypeLabels(
  t: TFunction,
  raw: string | undefined,
): string[] {
  if (!raw?.trim()) {
    return [];
  }
  const normalized = raw.trim();
  const slug = normalized
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .join("_");
  const i18nKey = slug
    ? `partners.destinationTypes.${slug}`
    : "partners.destinationTypes.unknown";
  return [t(i18nKey, { defaultValue: normalized })];
}

export function normalizeDestinationType(
  raw: string | null | undefined,
): GuesteriaPartnerDestinationType | null {
  const value = raw?.trim().toUpperCase();
  return value === "COUNTRY" || value === "REGION" || value === "CITY"
    ? value
    : null;
}

/** Fixed "Guesteria Partners" identity — no provider-specific branding at this scope. */
const GUESTERIA_PARTNERS_LOGO: GuesteriaPartnerRow["logo"] = {
  letter: "G",
  className: "bg-slate-600",
};

/** Stable id for the single "Guesteria Partners" row — kept as a function for call-site continuity. */
export function providerSlugFromSettings(): string {
  return GUESTERIA_PARTNERS_ROW_ID;
}

export function mapPartnerSettingsToRow(
  settings: PartnerSettings,
  t: TFunction,
  flagEmoji?: string | null,
): GuesteriaPartnerRow {
  const name = t("partners.page.title");

  const city = (
    settings.destination?.destinationName ?? settings.destinationName
  )?.trim();
  const resolvedFlagEmoji = settings.destination?.flagEmoji ?? flagEmoji ?? "";
  const region =
    city !== undefined && city.length > 0
      ? { flagEmoji: resolvedFlagEmoji, city }
      : undefined;

  return {
    id: GUESTERIA_PARTNERS_ROW_ID,
    enabled: settings.enabled,
    name,
    logo: GUESTERIA_PARTNERS_LOGO,
    destinationType: normalizeDestinationType(
      settings.destination?.destinationType ?? settings.destinationType,
    ),
    connectionStatus: settings.enabled ? "installed" : "unavailable",
    region,
    revenueEur:
      settings.lastMonthRevenue != null ? settings.lastMonthRevenue : undefined,
  };
}

export function mapPartnerSettingsToDetail(
  settings: PartnerSettings,
  t: TFunction,
  flagEmoji?: string | null,
): GuesteriaPartnerDetail {
  const row = mapPartnerSettingsToRow(settings, t, flagEmoji);
  const description = t(
    "partners.detail.providerDescriptions.guesteria_partners",
    {
      defaultValue: t("partners.detail.descriptionFallback"),
    },
  );

  const categoryLabels = destinationTypeLabels(t, settings.destinationType);

  return {
    ...row,
    description,
    categoryLabels,
  };
}
